import type { AdminMemberPortfolio, PortfolioCard } from "../api/types";
import { formatDate, formatDateTime, formatUsdString } from "../format";
import { useCompact } from "../hooks";
import { Counter } from "./Counter";
import { ErrorNotice, Loading } from "./Notice";
import { TokenLogo } from "./TokenLogo";
import { Section } from "./Section";

/**
 * A member's net worth as their own Portfolio screen composes it: the total,
 * one tile per slice, then the holdings behind each tile.
 *
 * `partial` is the only thing here that needs care. The API serves a failed
 * slice as zero rather than failing the read, so a total can be an
 * understatement, and the note below the tiles says exactly which part is
 * missing. Never hide the total when it is partial: an operator on a call
 * still needs the number they can vouch for, with the caveat beside it.
 */
export function PortfolioPanel({
  portfolio,
  loading,
  error,
}: {
  portfolio: AdminMemberPortfolio | null;
  loading: boolean;
  error: unknown;
}) {
  return (
    <Section
      title="Portfolio"
      aside={
        portfolio ? (
          <span className="subtle">As of {formatDateTime(portfolio.asOf)}</span>
        ) : null
      }
    >
      <div className="panel-body">
        {loading ? <Loading what="portfolio" /> : null}
        <ErrorNotice error={error} />
        {portfolio ? <PortfolioBody portfolio={portfolio} /> : null}
      </div>
    </Section>
  );
}

function PortfolioBody({ portfolio }: { portfolio: AdminMemberPortfolio }) {
  const { wallet, card, earn } = portfolio;
  const gaps = describeGaps(portfolio);
  const compact = useCompact();

  return (
    <div className="stack stack-loose">
      <div className="counters">
        <Counter label="Total" value={formatUsdString(portfolio.totalUsd)} money />
        <Counter
          label="Wallet"
          value={wallet.failed ? "—" : formatUsdString(wallet.totalUsd)}
          money
        />
        <Counter
          label="Card"
          value={card.failed ? "—" : formatUsdString(card.totalUsd)}
          money
        />
        <Counter
          label="Earn"
          value={earn.failed ? "—" : formatUsdString(earn.totalUsd)}
          money
        />
        <Counter
          label="Locked in Earn"
          value={earn.failed ? "—" : formatUsdString(earn.lockedUsd)}
          money
        />
      </div>

      {portfolio.partial ? (
        <div className="notice" role="status">
          <strong>This total is incomplete.</strong> {gaps.join(" ")}
        </div>
      ) : null}

      {wallet.tokens.length > 0 && compact ? (
        <div className="stack">
          <span className="subtle">Wallet holdings</span>
          <ul className="list list-inset">
            {wallet.tokens.map((token) => (
              <li key={`${token.chainId}:${token.address}`} className="list-row">
                <span className="list-main">
                  <span className="list-title">
                    <span className="asset">
                      <TokenLogo url={token.logoUrl} symbol={token.symbol} />
                      {token.symbol}
                    </span>
                  </span>
                  <span className="list-meta">
                    <span>Chain {token.chainId}</span>
                  </span>
                </span>
                <span className="list-trail list-amount">
                  {token.usdValue === null ? (
                    <span className="chip chip-muted">Unpriced</span>
                  ) : (
                    <span>{formatUsdString(token.usdValue)}</span>
                  )}
                  <span className="subtle">{token.amount}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : wallet.tokens.length > 0 ? (
        <div className="stack">
          <span className="subtle">Wallet holdings</span>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Chain</th>
                  <th className="num">Amount</th>
                  <th className="num">Value</th>
                </tr>
              </thead>
              <tbody>
                {wallet.tokens.map((token) => (
                  <tr key={`${token.chainId}:${token.address}`}>
                    <td className="cell-strong">
                      <span className="asset">
                        <TokenLogo url={token.logoUrl} symbol={token.symbol} />
                        {token.symbol}
                      </span>
                    </td>
                    <td>{token.chainId}</td>
                    <td className="num">{token.amount}</td>
                    <td className="num">
                      {token.usdValue === null ? (
                        <span className="chip chip-muted">Unpriced</span>
                      ) : (
                        formatUsdString(token.usdValue)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {card.cards.length > 0 ? (
        <div className="stack">
          <span className="subtle">Cards</span>
          <div className="row row-top">
            {card.cards.map((c) => (
              <CardTile key={c.id} card={c} />
            ))}
          </div>
        </div>
      ) : null}

      {earn.positions.length > 0 && compact ? (
        <div className="stack">
          <span className="subtle">Earn positions</span>
          <ul className="list list-inset">
            {earn.positions.map((p) => (
              <li key={p.vaultId} className="list-row">
                <span className="list-main">
                  <span className="list-title">
                    <span className="list-title-text">{p.vaultId}</span>
                  </span>
                  <span className="list-meta">
                    {Number(p.committedAmount) > 0 ? (
                      <span>
                        {formatUsdString(p.committedUsd)} locked
                        {p.committedUntil ? ` until ${formatDate(p.committedUntil)}` : ""}
                      </span>
                    ) : (
                      <span>Not locked</span>
                    )}
                  </span>
                </span>
                <span className="list-trail list-amount">
                  <span>{formatUsdString(p.assetsUsd)}</span>
                  <span className="subtle">{p.assetSymbol}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : earn.positions.length > 0 ? (
        <div className="stack">
          <span className="subtle">Earn positions</span>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vault</th>
                  <th>Asset</th>
                  <th className="num">Value</th>
                  <th className="num">Locked</th>
                  <th>Locked until</th>
                </tr>
              </thead>
              <tbody>
                {earn.positions.map((p) => (
                  <tr key={p.vaultId}>
                    <td className="cell-strong">{p.vaultId}</td>
                    <td>{p.assetSymbol}</td>
                    <td className="num">{formatUsdString(p.assetsUsd)}</td>
                    <td className="num">
                      {Number(p.committedAmount) > 0 ? (
                        <>
                          <div>{formatUsdString(p.committedUsd)}</div>
                          <div className="subtle">
                            {p.committedAmount} {p.assetSymbol}
                          </div>
                        </>
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td>
                      {p.committedUntil ? (
                        formatDate(p.committedUntil)
                      ) : (
                        <span className="subtle">Not locked</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The app's card artwork per membership tier, so an operator sees the card the
 * member sees. The rule is the app's own (`cardArtVariant` in
 * apps/mobile/components/cards/BankCard.tsx): tier 1 is Privé and gets the
 * gold art; everything else is the base art. The PNGs in public/cards are
 * downscaled copies of apps/mobile/assets/cards/card-art-*.png.
 */
const PRIVE_TIER_ID = 1;

function cardArt(tierId: number): { src: string; tier: string } {
  return tierId === PRIVE_TIER_ID
    ? { src: `${import.meta.env.BASE_URL}cards/card-art-prive.png`, tier: "Privé" }
    : { src: `${import.meta.env.BASE_URL}cards/card-art-base.png`, tier: "Base" };
}

function CardTile({ card }: { card: PortfolioCard }) {
  const tone =
    card.status === "active"
      ? "chip-ok"
      : card.status === "frozen"
        ? "chip-neutral"
        : "chip-bad";
  const art = cardArt(card.tierId);
  return (
    <div className="card-tile">
      <img className="card-art" src={art.src} alt={`${art.tier} card`} />
      <div className="stack stack-tight">
        <span className="cell-strong">{card.label}</span>
        <span className="row row-tight">
          <span className="mono-inline">
            •••• {card.last4}
          </span>
          <span className="subtle">{art.tier}</span>
          <span className={`chip ${tone}`}>{card.status}</span>
        </span>
      </div>
    </div>
  );
}

/** One sentence per gap, in the order the tiles sit. */
function describeGaps(portfolio: AdminMemberPortfolio): string[] {
  const { wallet, card, earn } = portfolio;
  const gaps: string[] = [];
  if (wallet.failed) gaps.push("Wallet balances could not be read.");
  else if (wallet.unreachableChainIds.length > 0) {
    gaps.push(
      `Chain ${wallet.unreachableChainIds.join(", ")} could not be reached, so the wallet figure is missing those balances.`,
    );
  }
  if (wallet.tokens.some((t) => t.usdValue === null && Number(t.amount) > 0)) {
    gaps.push("A held token has no price and is not counted.");
  }
  if (card.failed) gaps.push("The card balance could not be read.");
  if (earn.failed) gaps.push("Earn positions could not be read.");
  return gaps;
}
